import { observer } from 'mobx-react-lite';
import React, { Fragment, useContext, useEffect } from 'react';
import {
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Container } from 'semantic-ui-react';

import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import CsvUploadForm from '../../features/depositSalesTickets/csvUpload/CsvUploadForm';
import DepositSalesTicketDashboard from '../../features/depositSalesTickets/dashboard/DepositSalesTicketDashboard';
import DepositSalesTicketDetails from '../../features/depositSalesTickets/details/DepositSalesTicketDetails';
import DepositSalesTicketChangeLimitForm from '../../features/depositSalesTickets/form/DepositSalesTicketChangeLimitForm';
import DepositSalesTicketForm from '../../features/depositSalesTickets/form/DepositSalesTicketForm';
import PrintDashboard from '../../features/depositSalesTickets/print/PrintDashboard';
import NavBar from '../../features/nav/NavBar';
import QrTicketDashboard from '../../features/qrTickets/dashboard/QrTicketDashboard';
import ReservationDashboard from '../../features/reservations/dashboard/ReservationDashboard';
import FrameDashboard from '../../features/reservations/frame/FrameDashboard';
import SignInPage from '../../features/signin/SignInPage';
import { RootStoreContext } from '../stores/rootStore';
import LoadingComponent from './LoadingComponent';
import NotFound from './NotFound';
import NotFoundForAdmin from './NotFoundForAdmin';
import PrivateRoute from './PrivateRoute';

function getCookieValue(key: any) {
  const cookie = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(key));

  return cookie ? cookie.split('=')[1] : null;
}

if (process.env.REACT_APP_APPINSIGHTS_INSTRUMENTATION_KEY) {
  const telemetryInitializer = (envelope: any) => {
    const environment = getCookieValue('x-ms-routing-name') || 'production';
    envelope.data['slot'] = environment;
  };
  const appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: process.env.REACT_APP_APPINSIGHTS_INSTRUMENTATION_KEY,
    },
  });
  appInsights.loadAppInsights();
  appInsights.trackPageView();
  appInsights.addTelemetryInitializer(telemetryInitializer);
}

const App: React.FC<RouteComponentProps> = ({ location }) => {
  const rootStore = useContext(RootStoreContext);
  const { setAppLoaded, token, appLoaded } = rootStore.commonStore;
  const { getClerk } = rootStore.clerkStore;

  useEffect(() => {
    if (token) {
      getClerk().finally(() => setAppLoaded());
    } else {
      setAppLoaded();
    }
  }, [getClerk, setAppLoaded, token]);

  if (!appLoaded) return <LoadingComponent content='Loading...' />;

  return (
    <Fragment>
      <ToastContainer position='bottom-right' />
      <Switch>
        <Route exact path='/' component={ReservationDashboard} />
        <Route exact path='/admin' component={SignInPage} />
        <Route
          path={'/admin/(.+)'}
          render={() => (
            <Fragment>
              <NavBar />
              <Container style={{ marginTop: '5em' }}>
                <Switch>
                  <PrivateRoute
                    exact
                    path='/admin/depositsales'
                    component={DepositSalesTicketDashboard}
                  />
                  <PrivateRoute
                    exact
                    path='/admin/depositsales/detail/:id'
                    component={DepositSalesTicketDetails}
                  />
                  <PrivateRoute
                    key={location.key}
                    path={[
                      '/admin/depositsales/create',
                      '/admin/depositsales/edit/:id',
                    ]}
                    component={DepositSalesTicketForm}
                  />
                  <PrivateRoute
                    key={location.key}
                    path={['/admin/depositsales/changeLimit/:id']}
                    component={DepositSalesTicketChangeLimitForm}
                  />
                  <PrivateRoute
                    path={['/admin/depositsales/upload']}
                    component={CsvUploadForm}
                  />
                  <PrivateRoute
                    path={['/admin/depositsales/print']}
                    component={PrintDashboard}
                  />
                  <PrivateRoute
                    path={['/admin/reservations/createFrame']}
                    component={FrameDashboard}
                  />
                  <PrivateRoute
                    exact
                    path='/admin/depositsales/qrticket/:id'
                    component={QrTicketDashboard}
                  />
                  <Route component={NotFoundForAdmin} />
                </Switch>
              </Container>
            </Fragment>
          )}
        />
        <Route component={NotFound} />
      </Switch>
    </Fragment>
  );
};

export default withRouter(observer(App));
